export const productsData = [
  {
    slug: "demand-forecasting",
    heading: "Demand Forecasting",
    tagline:
      "Turn uncertainty into confident decisions with AI-native forecasting.",
    overViewTagLine: "Precision planning built for dynamic supply chains.",
    overViewDesc:
      "Gain visibility into demand variability across regions, product hierarchies, and customer segments. KGS Technology enables planners to simulate scenarios, measure forecast bias, and align inventory strategies with real-time market shifts.",
    overViewOpints: [
      "SKU, plant, region, and channel-level forecasting",
      "Seasonality, trend, and promotional uplift modeling",
      "Demand anomaly detection and alerting",
    ],
    capabilities: [
      "SKU, plant, region, and channel-level forecasting",
      "Seasonality, trend, and promotional uplift modeling",
      "Demand anomaly detection and alerting",
      "Scenario simulation for pricing, supply, and capacity",
      "Continuous forecast recalibration with real-time data",
      "External market signal integration (weather, economic indicators)",
      "External market signal integration (weather, economic indicators)",
      "Automated bias correction and forecast accuracy scoring",
    ],
    impact: [
      "Improved forecast accuracy (15–25% typical improvement)",
      "Reduced excess inventory and emergency production shifts",
      "Stronger revenue predictability and budget confidence",
      "Improved cross-functional planning alignment",
      "Lower safety stock requirements through precision forecasting",
      "Reduced waste and markdown costs from overproduction",
    ],
    metrics: [
      {
        value: "25%",
        label: "Forecast Accuracy Improvement",
      },
      {
        value: "3x",
        label: "Faster Planning Cycles",
      },
      {
        value: "40%",
        label: "Reduction in Safety Stock",
      },
      {
        value: "18%",
        label: "Revenue Uplift",
      },
    ],
    useCases: [
      {
        title: "",
        description:
          "Automatically adjust forecasts for seasonal peaks, promotional surges, and market shifts without manual intervention.",
      },
      {
        title: "New Product Launch",
        description:
          "Leverage analog modeling and market signals to predict demand for products with no historical baseline.",
      },
      {
        title: "Multi-Channel Optimization",
        description:
          "Synchronize forecasts across retail, e-commerce, wholesale, and direct channels for unified planning.",
      },
    ],
    integrations: [
      "SAP",
      "Oracle",
      "Microsoft Dynamics",
      "NetSuite",
      "Shopify",
      "Custom ERP",
    ],
    faq: [
      {
        question: "How quickly can we see results?",
        answer:
          "Most customers see measurable forecast accuracy improvements within the first 30 days of deployment.",
      },
      {
        question: "Does it work with limited historical data?",
        answer:
          "Yes. Our models leverage external market signals and analog products to generate reliable forecasts even with sparse history.",
      },
    ],
    getStarted: {
      badge: "Forecasting Intelligence",
      headline: "How Our AI Thinks Differently",
      subtitle:
        "Three layers of intelligence that separate precision from guesswork.",
      steps: [
        {
          num: "Signal Fusion",
          title: "Multi-Source Demand Sensing",
          desc: "POS transactions, weather patterns, economic indicators, and social trends merge into a unified demand signal — not just historical sales replayed forward.",
        },
        {
          num: "Model Selection",
          title: "Algorithm-Per-SKU Matching",
          desc: "Each SKU-location pair gets its own best-fit model. Seasonal items use decomposition. New launches use analog transfer. Stable products use exponential smoothing.",
        },
        {
          num: "Continuous Learning",
          title: "Self-Correcting Accuracy",
          desc: "Models retrain weekly against actuals. Bias detection flags systematic over- or under-forecasting before it compounds into costly inventory mistakes.",
        },
      ],
    },
    whyChoose: {
      headline: "Beyond Spreadsheet Planning",
      subtitle:
        "Built for volatile markets where yesterday's trends fail tomorrow.",
      items: [
        {
          title: "Multi-Horizon Accuracy",
          desc: "Generate weekly, monthly, and quarterly forecasts simultaneously — each tuned to different planning horizons with independent accuracy scoring and confidence intervals.",
          variant: "accent",
        },
        {
          title: "Promotional Uplift Modeling",
          desc: "Isolate the true demand lift from promotions, price changes, and marketing campaigns — preventing overstock hangovers that erode margins after events end.",
          variant: "teal",
        },
        {
          title: "Anomaly Detection & Auto-Correction",
          desc: "Flag demand spikes caused by one-time events (bulk orders, data errors) and automatically exclude them from baseline models to keep forecasts clean.",
          variant: "accent",
        },
        {
          title: "Scenario Simulation Engine",
          desc: "Model 'what-if' scenarios — new product launches, supplier disruptions, tariff changes — and quantify inventory and revenue impact before committing resources.",
          variant: "teal",
        },
      ],
    },

    platformHighlight: {
      headline: "Demand Intelligence Hub",
      subheadline: "Forecast with Conviction",
      description:
        "Demand Forecasting gives planners, merchandisers, and supply chain leaders a single view of projected demand across every SKU, location, and channel — with the confidence intervals needed to make bold inventory bets.",
      bullets: [
        "Forecast accuracy leaderboard ranked by SKU, region, and planner",
        "Bias tracking dashboard showing systematic over- and under-forecasting trends",
        "Integrated promotional calendar with uplift overlays per campaign",
        "One-click scenario comparison with side-by-side inventory impact analysis",
      ],
      stats: [
        {
          value: "25%",
          label: "Accuracy Gain",
          variant: "accent",
        },
        {
          value: "40%",
          label: "Less Safety Stock",
          variant: "teal",
        },
        {
          value: "3x",
          label: "Faster Cycles",
          variant: "accent",
        },
        {
          value: "< 30d",
          label: "Time to Value",
          variant: "teal",
        },
      ],
    },
  },
  {
    slug: "inventory-intelligence",
    heading: "Inventory Intelligence (MIST)",
    tagline:
      "MIST — Autonomous inventory agents that think ahead so you don't have to.",
    overViewTagLine: "Smarter Inventory. Stronger Performance.",
    overViewDesc:
      "MIST continuously analyzes demand patterns, supply variability, and network constraints to recommend optimal stock levels across locations. It reduces excess inventory, prevents stockouts, and improves service levels all while protecting working capital.",
    overViewOpints: [
      "Dynamic safety stock recalculation based on demand variability",
      "Network-wide inventory balancing across all locations",
      "Predictive stockout and overstock alerts with root-cause analysis",
    ],
    capabilities: [
      "Dynamic safety stock recalculation based on demand variability",
      "Network-wide inventory balancing across all locations",
      "Predictive stockout and overstock alerts with root-cause analysis",
      "Lead-time variability modeling and supplier reliability scoring",
      "Automated replenishment recommendations with approval workflows",
      "ABC/XYZ classification with AI-driven segmentation",
      "Shelf-life and expiration management with FEFO optimization",
      "Multi-echelon inventory optimization across supply tiers",
    ],
    impact: [
      "Reduction in stockouts (10–20% typical range)",
      "Improved inventory turns and warehouse throughput",
      "Lower carrying costs and reduced obsolescence write-offs",
      "Better working capital utilization and cash flow management",
      "Enhanced fill rates and customer service levels",
      "Reduced emergency expediting and premium freight costs",
    ],
    metrics: [
      {
        value: "20%",
        label: "Stockout Reduction",
      },
      {
        value: "15%",
        label: "Carrying Cost Savings",
      },
      {
        value: "2.5x",
        label: "Inventory Turns Improvement",
      },
      {
        value: "99.2%",
        label: "Service Level Achievement",
      },
    ],
    useCases: [
      {
        title: "Multi-Warehouse Balancing",
        description:
          "Automatically redistribute inventory across locations based on real-time demand signals and lead times.",
      },
      {
        title: "Predictive Replenishment",
        description:
          "Generate purchase orders before stockouts occur using AI-driven reorder point optimization.",
      },
      {
        title: "Excess Inventory Liquidation",
        description:
          "Identify slow-moving SKUs early and trigger markdown or redistribution workflows automatically.",
      },
    ],
    integrations: [
      "SAP WM",
      "Oracle WMS",
      "Manhattan Associates",
      "Blue Yonder",
      "Custom WMS",
    ],
    faq: [
      {
        question: "How does MIST handle demand volatility?",
        answer:
          "MIST continuously recalculates safety stock using real-time demand signals, adjusting dynamically to market changes.",
      },
      {
        question: "Can it manage perishable inventory?",
        answer:
          "Yes, MIST supports FIFO/FEFO rules and shelf-life tracking for perishable goods.",
      },
    ],
    getStarted: {
      badge: "Autonomous Operations",
      headline: "The Closed-Loop Replenishment Cycle",
      subtitle:
        "Sense demand shifts, calculate optimal levels, execute without hesitation.",
      steps: [
        {
          num: "Sense",
          title: "Real-Time Position Awareness",
          desc: "Every warehouse, DC, and retail location reports live stock levels. Demand signals feed directly into the calculation engine — no batch delays.",
        },
        {
          num: "Decide",
          title: "AI-Optimized Reorder Logic",
          desc: "Dynamic safety stock, economic order quantities, and lead-time variability combine into purchase recommendations tuned to your service-level targets.",
        },
        {
          num: "Execute",
          title: "Hands-Free Order Generation",
          desc: "High-confidence replenishment orders flow directly into your ERP. Only edge cases surface for human review — your team manages exceptions, not routine.",
        },
      ],
    },
    whyChoose: {
      headline: "Beyond Manual Stock Planning",
      subtitle:
        "For networks where overstocking costs as much as understocking.",
      items: [
        {
          title: "Dynamic Safety Stock",
          desc: "Safety stock isn't static — it recalculates daily based on demand variability, lead-time fluctuations, and service-level targets per SKU-location pair.",
          variant: "accent",
        },
        {
          title: "Network-Wide Balancing",
          desc: "Identify surplus at one warehouse and deficit at another, then generate transfer orders that rebalance the network without new procurement spend.",
          variant: "teal",
        },
        {
          title: "Shelf-Life & Expiration Control",
          desc: "FEFO logic ensures perishable goods ship in the right sequence, with automated alerts when items approach expiry thresholds — preventing costly write-offs.",
          variant: "accent",
        },
        {
          title: "Working Capital Optimization",
          desc: "Quantify the cash trapped in excess inventory and model the working capital released by tightening stock levels — giving CFOs the numbers they need.",
          variant: "teal",
        },
      ],
    },
    platformHighlight: {
      headline: "Control Tower for Every SKU",
      subheadline: "Stock Smarter, Not More",
      description:
        "MIST gives supply chain teams, warehouse managers, and finance leaders a unified view of every item across every location — with AI-recommended actions to keep service levels high and costs low.",
      bullets: [
        "SKU health scorecards showing velocity, days-of-supply, and aging risk",
        "Automated redistribution recommendations across the warehouse network",
        "Slow-mover identification with markdown and liquidation triggers",
        "Working capital impact calculator for every replenishment decision",
      ],
      stats: [
        {
          value: "20%",
          label: "Fewer Stockouts",
          variant: "accent",
        },
        {
          value: "15%",
          label: "Lower Carrying Cost",
          variant: "teal",
        },
        {
          value: "2.5x",
          label: "Better Turns",
          variant: "accent",
        },
        {
          value: "99.2%",
          label: "Service Level",
          variant: "teal",
        },
      ],
    },
  },
  {
    slug: "order-planning",
    heading: "Order Planning",
    tagline:
      "Orchestrate every order from placement to delivery with AI precision.",
    overViewTagLine: "Gain Complete Control Over Every Order",
    overViewDesc:
      "From purchase order creation to final delivery, Order Planning provides real-time visibility across the entire lifecycle.It proactively detects delays, recommends corrective actions, and ensures orders stay on track even when disruptions occur.",
    overViewOpints: [
      "AI-based route optimization with multi-constraint solving",
      "Real-time re-routing and dynamic lane assignment",
      "PO-to-delivery milestone tracking with automated alerts",
    ],
    capabilities: [
      "AI-based route optimization with multi-constraint solving",
      "Real-time re-routing and dynamic lane assignment",
      "PO-to-delivery milestone tracking with automated alerts",
      "Risk scoring and SLA monitoring at every checkpoint",
      "Automated escalation triggers and exception management",
      "Carrier performance benchmarking and scoring",
      "Load consolidation and multi-stop optimization",
      "Predictive delay detection with proactive notifications",
    ],
    impact: [
      "Freight cost optimization (8–15% reduction)",
      "Improved OTIF performance and delivery reliability",
      "Faster exception resolution with automated workflows",
      "Enhanced customer reliability and trust scores",
      "Reduced manual planning effort by up to 40%",
      "Better carrier utilization and lane optimization",
    ],
    metrics: [
      {
        value: "15%",
        label: "Freight Cost Reduction",
      },
      {
        value: "97%",
        label: "OTIF Performance",
      },
      {
        value: "60%",
        label: "Faster Exception Resolution",
      },
      {
        value: "12%",
        label: "Route Optimization Savings",
      },
    ],
    useCases: [
      {
        title: "Cross-Border Shipment Orchestration",
        description:
          "Manage complex multi-leg international shipments with automated documentation and customs clearance.",
      },
      {
        title: "Disruption Recovery",
        description:
          "Automatically reroute shipments and notify stakeholders when delays or exceptions are detected.",
      },
      {
        title: "Carrier Selection Optimization",
        description:
          "AI-driven carrier scoring and selection based on cost, reliability, and capacity availability.",
      },
    ],
    integrations: [
      "SAP TM",
      "Oracle OTM",
      "BluJay",
      "MercuryGate",
      "Descartes",
    ],
    faq: [
      {
        question: "How does the AI handle disruptions?",
        answer:
          "Our system monitors 50+ risk signals and automatically triggers re-routing, carrier switching, and stakeholder notifications.",
      },
      {
        question: "Does it support multi-modal shipments?",
        answer:
          "Yes, it supports ocean, air, rail, and ground transportation with seamless handoff management.",
      },
    ],
    getStarted: {
      badge: "Predictive Control",
      headline: "A Three-Stage Defense System",
      subtitle:
        "Detect risk early, intervene autonomously, keep every stakeholder informed.",
      steps: [
        {
          num: "48h Horizon",
          title: "Early Risk Scoring",
          desc: "Every active shipment carries a delay probability score updated hourly. When scores cross your threshold, intervention triggers before impact reaches the customer.",
        },
        {
          num: "Smart Routing",
          title: "Autonomous Path Recovery",
          desc: "AI evaluates alternative carriers, routes, and modes in real time — selecting the fastest recovery path that meets both cost and SLA constraints.",
        },
        {
          num: "Stakeholder Sync",
          title: "Cascading Notifications",
          desc: "When a shipment re-routes, every affected party — warehouse, customer, finance — receives tailored updates with revised ETAs and impact summaries.",
        },
      ],
    },
    whyChoose: {
      headline: "Eliminate Shipment Firefighting",
      subtitle: "Stop chasing shipments across spreadsheets and email chains.",
      items: [
        {
          title: "Predictive Delay Detection",
          desc: "Flag shipments likely to miss their delivery window 48–72 hours in advance — giving planners time to act instead of react.",
          variant: "accent",
        },
        {
          title: "Load Consolidation Engine",
          desc: "Automatically identify orders heading to the same region and consolidate them into fewer, fuller loads — cutting per-unit freight costs by 8–15%.",
          variant: "teal",
        },
        {
          title: "Carrier Performance Scoring",
          desc: "Rank carriers by actual on-time rate, damage claims, and cost-per-mile — replacing gut-feel decisions with objective performance benchmarks.",
          variant: "accent",
        },
        {
          title: "Exception Audit Trail",
          desc: "Every disruption, re-route, and carrier switch is logged with timestamps and reasoning — giving compliance teams a complete decision history.",
          variant: "teal",
        },
      ],
    },
    platformHighlight: {
      headline: "Shipment Orchestration Hub",
      subheadline: "Zero Blind Spots",
      description:
        "Order Planning gives logistics coordinators, planners, and customer service teams a single live view of every order's journey — from creation through final delivery — with AI-powered intervention at every risk point.",
      bullets: [
        "Live shipment map with color-coded risk status and delay predictions",
        "Automated carrier selection based on cost, reliability, and capacity",
        "Exception queue with AI-recommended resolution actions",
        "OTIF performance dashboard with drill-down by lane, carrier, and customer",
      ],
      stats: [
        {
          value: "15%",
          label: "Freight Savings",
          variant: "accent",
        },
        {
          value: "97%",
          label: "OTIF Rate",
          variant: "teal",
        },
        {
          value: "60%",
          label: "Faster Resolution",
          variant: "accent",
        },
        {
          value: "48h",
          label: "Early Warning",
          variant: "teal",
        },
      ],
    },
  },
  {
    slug: "lite-tms",
    heading: "Freightmind TMS for SMBs",
    tagline:
      "Enterprise-grade transportation visibility without enterprise complexity.",
    overViewTagLine: "Discover Freightmind TMS for SMBs",
    overViewDesc:
      "Lite TMS eliminates manual shipment tracking, disconnected spreadsheets, and carrier communication gaps. It provides a centralized platform to plan, track, and manage transportation workflows with minimal setup and rapid go-live.",
    overViewOpints: [
      "Load creation, tendering, and real-time tracking",
      "Driver and fleet scheduling with calendar integration",
      "Freight billing automation and invoice reconciliation",
    ],
    capabilities: [
      "Load creation, tendering, and real-time tracking",
      "Driver and fleet scheduling with calendar integration",
      "Freight billing automation and invoice reconciliation",
      "Performance KPI monitoring with custom dashboards",
      "Carrier rate management and comparison tools",
      "Digital proof-of-delivery and signature capture",
      "Automated shipment status notifications to stakeholders",
      "Route history analytics and lane performance insights",
    ],
    impact: [
      "Improved fleet utilization and reduced deadhead miles",
      "Reduced manual coordination by up to 50%",
      "Greater shipment accountability and visibility",
      "Faster billing cycles and reduced payment disputes",
      "Lower operational overhead for growing logistics teams",
      "Improved customer communication with real-time updates",
    ],
    metrics: [
      {
        value: "30%",
        label: "Fleet Utilization Improvement",
      },
      {
        value: "50%",
        label: "Reduction in Manual Tasks",
      },
      {
        value: "4 weeks",
        label: "Average Go-Live Time",
      },
      {
        value: "85%",
        label: "Shipment Visibility Coverage",
      },
    ],
    useCases: [
      {
        title: "Regional Fleet Management",
        description:
          "Track and optimize regional delivery fleets with real-time GPS and automated dispatch scheduling.",
      },
      {
        title: "Freight Billing Automation",
        description:
          "Eliminate manual invoice creation with automated rate calculation and billing workflows.",
      },
      {
        title: "Performance Monitoring",
        description:
          "Track driver performance, on-time rates, and fleet KPIs through intuitive dashboards.",
      },
    ],
    integrations: [
      "QuickBooks",
      "Xero",
      "Google Maps",
      "Samsara",
      "KeepTruckin",
    ],
    faq: [
      {
        question: "Is this suitable for my fleet size?",
        answer:
          "Lite TMS is designed for SMBs with 5–100 vehicles, offering scalable plans that grow with your business.",
      },
      {
        question: "How long does implementation take?",
        answer:
          "Most customers are fully operational within 2–4 weeks with our guided onboarding process.",
      },
    ],
    getStarted: {
      badge: "Growth Journey",
      headline: "From First Load to Full Fleet",
      subtitle: "A platform that scales with you — not one you outgrow.",
      steps: [
        {
          num: "Day One",
          title: "Operational in a Single Session",
          desc: "Import your fleet roster, upload rate sheets, and create your first load — most operators dispatch within 30 minutes of signing up.",
        },
        {
          num: "Week Two",
          title: "Automated Billing Takes Over",
          desc: "Rates, fuel surcharges, and accessorials calculate at delivery. Invoices generate automatically — eliminating manual rate lookups and billing disputes.",
        },
        {
          num: "Month Three",
          title: "Strategic Intelligence Emerges",
          desc: "KPI dashboards reveal cost-per-mile trends, carrier reliability patterns, and fleet utilization gaps — turning operational noise into strategic clarity.",
        },
      ],
    },
    whyChoose: {
      headline: "Beyond Spreadsheet Logistics",
      subtitle: "Enterprise structure without the enterprise price tag.",
      items: [
        {
          title: "Zero-Complexity Onboarding",
          desc: "No consultants, no multi-month implementations. Lite TMS is self-serve — most operators build their first load within 30 minutes of signing up.",
          variant: "accent",
        },
        {
          title: "Automated Freight Billing",
          desc: "Rates, accessorials, and fuel surcharges calculate automatically at delivery. Invoices generate from shipment data — eliminating manual rate lookups and billing disputes.",
          variant: "teal",
        },
        {
          title: "Driver Mobile App",
          desc: "Drivers receive assignments, navigate routes, capture PODs, and update statuses from their phone — no hardware installs or training sessions required.",
          variant: "accent",
        },
        {
          title: "Scales With Your Growth",
          desc: "Start with 5 trucks or 50. Lite TMS pricing scales linearly — no surprise tier jumps or feature gates as your fleet expands.",
          variant: "teal",
        },
      ],
    },
    platformHighlight: {
      headline: "SMB Logistics Command Center",
      subheadline: "Simple Yet Complete",
      description:
        "Lite TMS gives fleet managers, dispatchers, and back-office teams a single platform that handles load creation, tracking, billing, and reporting — without requiring dedicated IT support.",
      bullets: [
        "Drag-and-drop load board with carrier tendering and acceptance tracking",
        "Live fleet map with GPS positions, ETA countdowns, and delay alerts",
        "Automated invoice generation tied to rate agreements and accessorial rules",
        "KPI dashboards tracking on-time rates, cost-per-mile, and fleet utilization",
      ],
      stats: [
        {
          value: "30%",
          label: "Better Utilization",
          variant: "accent",
        },
        {
          value: "< 4wk",
          label: "Go-Live Time",
          variant: "teal",
        },
        {
          value: "50%",
          label: "Less Manual Work",
          variant: "accent",
        },
        {
          value: "85%",
          label: "Visibility",
          variant: "teal",
        },
      ],
    },
  },

  {
    slug: "warehouse-management",
    heading: "Warehouse Management System",
    tagline:
      "Intelligent control for every warehouse movement, from dock to dispatch.",
    overViewTagLine: "Streamline Every Warehouse Workflow",
    overViewDesc:
      "From inbound receiving and put-away to picking, packing, and dispatch, KGS Technology WMS standardizes and automates daily warehouse tasks.",
    overViewOpints: [
      "Barcode and RFID integration for real-time inventory tracking",
      "Optimized picking paths with wave and batch planning",
      "FIFO / FEFO rule enforcement with automated rotation",
    ],
    capabilities: [
      "Barcode and RFID integration for real-time inventory tracking",
      "Optimized picking paths with wave and batch planning",
      "FIFO / FEFO rule enforcement with automated rotation",
      "SKU aging dashboards and slow-mover identification",
      "Putaway optimization based on velocity and dimensions",
      "Cross-docking support for time-sensitive shipments",
      "Labor productivity tracking and task assignment",
      "Dock scheduling and yard management integration",
    ],
    impact: [
      "15–25% faster picking cycles and order fulfillment",
      "Reduced shrinkage, spoilage, and inventory discrepancies",
      "Higher inventory accuracy (99%+ achievable)",
      "Improved labor utilization and reduced overtime costs",
      "Better space utilization through intelligent slotting",
      "Faster receiving-to-ship cycle times",
    ],
    metrics: [
      {
        value: "25%",
        label: "Faster Picking Cycles",
      },
      {
        value: "99.5%",
        label: "Inventory Accuracy",
      },
      {
        value: "35%",
        label: "Shrinkage Reduction",
      },
      {
        value: "20%",
        label: "Labor Efficiency Gain",
      },
    ],
    useCases: [
      {
        title: "High-Volume Distribution",
        description:
          "Manage thousands of daily orders with wave planning, zone picking, and automated sortation.",
      },
      {
        title: "Cold Storage Operations",
        description:
          "Monitor temperature zones, enforce FIFO/FEFO, and track lot-level compliance in cold environments.",
      },
      {
        title: "Returns Processing",
        description:
          "Streamline reverse logistics with automated return inspection, restocking, and disposition workflows.",
      },
    ],
    integrations: [
      "SAP EWM",
      "Oracle WMS",
      "Zebra",
      "Honeywell",
      "RFID Systems",
    ],
    faq: [
      {
        question: "Does it support barcode and RFID?",
        answer:
          "Yes, native integration with barcode scanners and RFID readers for real-time inventory tracking.",
      },
      {
        question: "Can it handle multiple warehouse locations?",
        answer:
          "Absolutely. Our WMS supports multi-site management with centralized visibility across all locations.",
      },
    ],
    getStarted: {
      badge: "Operational Flow",
      headline: "From Dock Door to Dispatch Bay",
      subtitle:
        "Every warehouse movement tracked, optimized, and verified in real time.",
      steps: [
        {
          num: "Inbound",
          title: "Receiving & Putaway Intelligence",
          desc: "Dock appointments, PO verification, and AI-directed putaway ensure every item reaches its optimal bin location — minimizing travel for downstream picks.",
        },
        {
          num: "Processing",
          title: "Wave Planning & Pick Optimization",
          desc: "Orders group into intelligent waves. Pick paths sequence by zone proximity — reducing walk time per order by 25% without adding headcount.",
        },
        {
          num: "Outbound",
          title: "Pack Verification & Ship Execution",
          desc: "Scan-based pack confirmation prevents mis-ships. Carrier labels auto-generate. Dock scheduling ensures trucks don't wait — and neither does your team.",
        },
      ],
    },
    whyChoose: {
      headline: "Transform Warehouse Profitability",
      subtitle: "Where accuracy, speed, and labor costs define your margin.",
      items: [
        {
          title: "Optimized Picking Paths",
          desc: "AI generates the shortest walk sequence for every pick wave — reducing travel time per order by 25% and letting the same crew ship more volume per shift.",
          variant: "accent",
        },
        {
          title: "Real-Time Inventory Accuracy",
          desc: "Every scan, putaway, and pick updates inventory counts instantly. Cycle count discrepancies drop below 0.5%, eliminating costly physical inventory shutdowns.",
          variant: "teal",
        },
        {
          title: "Cold Chain Compliance",
          desc: "Temperature zone monitoring, FEFO enforcement, and lot-level traceability keep perishable and regulated products audit-ready at all times.",
          variant: "accent",
        },
        {
          title: "Labor Productivity Tracking",
          desc: "Measure picks-per-hour, putaway speed, and idle time per worker — giving supervisors the metrics to coach performance and schedule shifts intelligently.",
          variant: "teal",
        },
      ],
    },
    platformHighlight: {
      headline: "Dock-to-Dispatch Visibility",
      subheadline: "Every Bin, Every Move",
      description:
        "Warehouse Management System gives warehouse managers, supervisors, and operations directors a real-time view of every inventory movement — with AI-optimized workflows that squeeze more throughput from existing space and labor.",
      bullets: [
        "Live floor heatmap showing zone activity, bottlenecks, and idle areas",
        "Wave planning dashboard with drag-and-drop order prioritization",
        "Dock scheduling board with inbound appointment management",
        "Shrinkage and discrepancy tracker with root-cause drill-down",
      ],
      stats: [
        {
          value: "25%",
          label: "Faster Picking",
          variant: "accent",
        },
        {
          value: "99.5%",
          label: "Accuracy",
          variant: "teal",
        },
        {
          value: "35%",
          label: "Less Shrinkage",
          variant: "accent",
        },
        {
          value: "20%",
          label: "Labor Savings",
          variant: "teal",
        },
      ],
    },
  },

  {
    slug: "dispatch-management",
    heading: "Dispatch Management System",
    tagline:
      "Master the last mile with real-time intelligence and automated dispatch.",
    overViewTagLine: "Optimize Every Dispatch Decision",
    overViewDesc:
      "Automatically assign drivers, sequence deliveries, and adjust routes based on real-time conditions. Dispatch Management ensures faster response to disruptions while maintaining consistent service levels.",
    overViewOpints: [
      "Smart load building and route sequencing",
      "Live GPS tracking with ETA prediction",
      "Geofence-triggered status updates and notifications",
    ],
    capabilities: [
      "Smart load building and route sequencing",
      "Live GPS tracking with ETA prediction",
      "Geofence-triggered status updates and notifications",
      "Digital POD capture with photo and signature support",
      "Driver communication and in-app messaging",
      "Customer delivery window management and scheduling",
      "Return and reverse logistics workflow automation",
      "Fleet maintenance scheduling and compliance tracking",
    ],
    impact: [
      "Improved on-time delivery rates by 20–30%",
      "Reduced last-mile operational costs by 15–25%",
      "Better fleet visibility and real-time decision-making",
      "Higher customer satisfaction through accurate ETAs",
      "Reduced failed deliveries and re-delivery attempts",
      "Lower fuel costs through optimized routing",
    ],
    metrics: [
      {
        value: "22%",
        label: "On-Time Delivery Improvement",
      },
      {
        value: "30%",
        label: "Last-Mile Cost Reduction",
      },
      {
        value: "95%",
        label: "Digital POD Adoption",
      },
      {
        value: "40%",
        label: "Fewer Customer Complaints",
      },
    ],
    useCases: [
      {
        title: "Same-Day Delivery Operations",
        description:
          "Optimize routes in real-time and dispatch dynamically to meet tight same-day delivery windows.",
      },
      {
        title: "Proof of Delivery Digitization",
        description:
          "Capture signatures, photos, and timestamps digitally to eliminate paperwork and disputes.",
      },
      {
        title: "Geofence-Based Automation",
        description:
          "Trigger automatic status updates, notifications, and billing when vehicles enter or exit predefined zones.",
      },
    ],
    integrations: [
      "Google Maps",
      "Waze",
      "Salesforce",
      "HubSpot",
      "Custom CRM",
    ],
    faq: [
      {
        question: "Does it work for B2B and B2C delivery?",
        answer:
          "Yes, our dispatch system supports both B2B multi-stop routes and B2C last-mile delivery scenarios.",
      },
      {
        question: "Can drivers use their own devices?",
        answer:
          "Yes, our mobile app works on iOS and Android with no special hardware required.",
      },
    ],
    getStarted: {
      badge: "Two-Sided Platform",
      headline: "Dispatchers Command, Drivers Execute",
      subtitle:
        "One platform, two experiences — both designed for speed and clarity.",
      steps: [
        {
          num: "Control Room",
          title: "Dispatcher Command Center",
          desc: "Live fleet map, drag-and-drop route editing, and exception queues give dispatchers total control over every vehicle and every stop in real time.",
        },
        {
          num: "On the Road",
          title: "Driver Mobile Experience",
          desc: "Turn-by-turn navigation, digital POD capture, and one-tap status updates — drivers focus on delivering, not on paperwork or phone calls.",
        },
        {
          num: "Customer Side",
          title: "Branded Tracking for Recipients",
          desc: "Auto-generated tracking links with live GPS position and countdown timers — reducing 'Where is my order?' calls by up to 60%.",
        },
      ],
    },
    whyChoose: {
      headline: "Win the Last Mile",
      subtitle: "Every missed delivery window costs revenue and reputation.",
      items: [
        {
          title: "Sub-Second Re-Routing",
          desc: "When a vehicle breaks down or a delivery is refused, the engine re-sequences remaining stops across the fleet in under one second.",
          variant: "accent",
        },
        {
          title: "Driver Scorecards",
          desc: "Track on-time rates, idle time, POD compliance, and customer ratings per driver — turning delivery into a measurable performance discipline.",
          variant: "teal",
        },
        {
          title: "Customer-Facing ETA Links",
          desc: "Auto-send branded tracking pages with live GPS position and a countdown timer — reducing 'Where is my order?' calls by up to 60%.",
          variant: "accent",
        },
        {
          title: "Reverse Logistics Built In",
          desc: "Handle pickups, returns, and exchanges within the same route plan — no separate workflows or manual scheduling required.",
          variant: "teal",
        },
      ],
    },
    platformHighlight: {
      headline: "Last-Mile Command Center",
      subheadline: "Total Last-Mile Control",
      description:
        "Dispatch Management gives dispatchers, fleet managers, and customer service teams a single live view of every vehicle, every stop, and every exception — from first pickup to final signature.",
      bullets: [
        "Live fleet map with vehicle status, speed, and next-stop countdown",
        "Drag-and-drop route editing for dispatchers handling exceptions",
        "Automated customer notifications triggered by geofence events",
        "Consolidated POD gallery with searchable photo and signature archive",
      ],
      stats: [
        {
          value: "< 1s",
          label: "Re-Route Speed",
          variant: "accent",
        },
        {
          value: "60%",
          label: "Fewer WISMO Calls",
          variant: "teal",
        },
        {
          value: "98.5%",
          label: "POD Capture Rate",
          variant: "teal",
        },
        {
          value: "24/7",
          label: "Fleet Visibility",
          variant: "accent",
        },
      ],
    },
  },
  {
    slug: "freight-forwarding",
    heading: "Freight Forwarding Platform",
    tagline: "Unify global trade under one intelligent digital platform.",
    overViewTagLine: "Streamline Cross-Border Operations End-to-End",
    overViewDesc:
      "Manage rate requests, shipment bookings, customs documentation, and milestone tracking within a single structured workflow. Eliminate manual coordination across emails, spreadsheets, and disconnected systems.",
    overViewOpints: [
      "Quote-to-book automation with rate management",
      "Predictive ETA tracking across ocean, air, and ground",
      "Carrier document management and digital filing",
    ],
    capabilities: [
      "Quote-to-book automation with rate management",
      "Predictive ETA tracking across ocean, air, and ground",
      "Carrier document management and digital filing",
      "Demurrage and detention monitoring with alerts",
      "Multi-modal shipment consolidation and routing",
      "Customs pre-clearance document preparation",
      "Container tracking with milestone-based visibility",
      "Automated carrier performance scoring and reporting",
    ],
    impact: [
      "Reduced detention and demurrage exposure by up to 40%",
      "Faster shipment booking and documentation cycles",
      "Improved landed cost visibility and margin control",
      "Enhanced compliance with international trade regulations",
      "Reduced manual data entry by 60% through automation",
      "Better carrier relationship management and negotiations",
    ],
    metrics: [
      {
        value: "45%",
        label: "Faster Quote Turnaround",
      },
      {
        value: "60%",
        label: "Detention Cost Reduction",
      },
      {
        value: "3x",
        label: "Document Processing Speed",
      },
      {
        value: "90%",
        label: "ETA Prediction Accuracy",
      },
    ],
    useCases: [
      {
        title: "Ocean Freight Management",
        description:
          "Manage bookings, container tracking, and demurrage monitoring across global ocean carriers.",
      },
      {
        title: "Air Cargo Operations",
        description:
          "Streamline air freight quoting, booking, and AWB management with carrier connectivity.",
      },
      {
        title: "Cross-Border Compliance",
        description:
          "Automate customs documentation, HS code classification, and regulatory compliance across jurisdictions.",
      },
    ],
    integrations: [
      "CargoWise",
      "Descartes",
      "INTTRA",
      "MarineTraffic",
      "FlightAware",
    ],
    faq: [
      {
        question: "How many carriers are supported?",
        answer:
          "We integrate with 100+ ocean and air carriers globally with direct API connectivity.",
      },
      {
        question: "Does it handle multi-modal shipments?",
        answer:
          "Yes, seamless management of ocean, air, and ground legs within a single shipment record.",
      },
    ],
    getStarted: {
      badge: "Trade Operations",
      headline: "Three Pillars of Digital Forwarding",
      subtitle:
        "Rate management, documentation, and tracking — unified under one intelligent platform.",
      steps: [
        {
          num: "Commercial",
          title: "Rate Intelligence & Quoting",
          desc: "Compare carrier rates across modes, generate customer quotes with margin targets, and confirm bookings — all without leaving the platform or opening a spreadsheet.",
        },
        {
          num: "Compliance",
          title: "Automated Document Generation",
          desc: "BOLs, commercial invoices, and customs declarations populate from booking details. HS codes auto-suggest. Filing deadlines track automatically.",
        },
        {
          num: "Operational",
          title: "Predictive Container Tracking",
          desc: "Vessel and flight feeds update ETAs continuously. Demurrage countdown alerts fire at configurable thresholds — protecting margins on every import.",
        },
      ],
    },
    whyChoose: {
      headline: "Replace Fragmented Tools",
      subtitle: "Built for forwarders managing complex multi-modal shipments.",
      items: [
        {
          title: "Unified Quote-to-Book",
          desc: "Compare rates across carriers, generate customer quotes, and confirm bookings in a single workflow — eliminating the back-and-forth between rate sheets, email, and booking portals.",
          variant: "accent",
        },
        {
          title: "Demurrage & Detention Shield",
          desc: "Real-time free-time tracking with escalating alerts at 48h, 24h, and same-day thresholds — preventing the container charges that silently erode margins on every import.",
          variant: "teal",
        },
        {
          title: "Multi-Modal Shipment Stitching",
          desc: "Ocean, air, and ground legs connect within a single shipment record with handoff tracking — no separate systems for each mode.",
          variant: "accent",
        },
        {
          title: "Carrier Performance Intelligence",
          desc: "Score carriers on transit time accuracy, booking reliability, and damage rates — giving procurement teams objective data for contract negotiations.",
          variant: "teal",
        },
      ],
    },
    platformHighlight: {
      headline: "Global Trade on One Screen",
      subheadline: "Border-to-Border Visibility",
      description:
        "Freight Forwarding Platform gives trade ops teams, customs brokers, and logistics managers a single command center for every shipment moving across international borders — with predictive intelligence at every milestone.",
      bullets: [
        "Global shipment map with real-time vessel and flight tracking overlays",
        "Demurrage countdown timers with cost projections per container",
        "Automated customs document package generation per destination country",
        "Carrier scorecard dashboard with cost, reliability, and transit-time rankings",
      ],
      stats: [
        {
          value: "45%",
          label: "Faster Quotes",
          variant: "accent",
        },
        {
          value: "60%",
          label: "Less Detention",
          variant: "teal",
        },
        {
          value: "90%",
          label: "ETA Accuracy",
          variant: "accent",
        },
        {
          value: "100+",
          label: "Carrier APIs",
          variant: "teal",
        },
      ],
    },
  },
  {
    slug: "edifai",
    heading: "EDIFAI",
    tagline:
      "Intelligence behind every transaction — AI-native EDI for enterprise-scale operations.",
    overViewTagLine: "Transform EDI From Reactive to Intelligent",
    overViewDesc:
      "EDIFAI continuously monitors inbound and outbound transactions, detects anomalies in real time, and automatically resolves common errors before they disrupt downstream systems. Operations teams gain stability without increasing manual oversight.",
    overViewOpints: [
      "Schema validation and risk detection before processing",
      "AI-assisted adaptive data mapping during transformation",
      "Automated tracking and SLA monitoring after delivery",
    ],
    capabilities: [
      "Schema validation and risk detection before processing",
      "AI-assisted adaptive data mapping during transformation",
      "Automated tracking and SLA monitoring after delivery",
      "Autonomous correction with full traceability when issues arise",
      "Support for X12, EDIFACT, XML, JSON, and CSV formats",
      "AS2, SFTP/FTPS, APIs, Webhooks, and VAN integrations",
      "Real-time business-friendly visibility across the entire EDI lifecycle",
      "Partner performance, exception trends, and SLA adherence monitoring",
    ],
    impact: [
      "65% faster issue resolution across EDI operations",
      "50% fewer transaction failures through proactive detection",
      "70% reduction in manual EDI workload",
      "Transforms EDI from reactive to predictive operations",
      "Faster partner onboarding with guided setup and testing",
      "Unified execution replacing fragmented integrations",
    ],
    metrics: [
      {
        value: "65%",
        label: "Faster Issue Resolution",
      },
      {
        value: "50%",
        label: "Fewer Transaction Failures",
      },
      {
        value: "70%",
        label: "Reduced Manual EDI Workload",
      },
      {
        value: "AI-Native",
        label: "EDI Intelligence",
      },
    ],
    useCases: [
      {
        title: "Enterprise-Scale EDI Operations",
        description:
          "Process thousands of daily transactions across X12, EDIFACT, and proprietary formats with AI that anticipates issues before they disrupt.",
      },
      {
        title: "Autonomous Error Remediation",
        description:
          "Auto-correct predictable errors — wrong UOM codes, missing segments, duplicate ISA numbers — and learn from recurring patterns.",
      },
      {
        title: "Real-Time EDI Visibility",
        description:
          "Plain-language transaction status for operations teams. Replace cryptic 997 FAs with clear, actionable updates.",
      },
      {
        title: "Multi-Standard Partner Onboarding",
        description:
          "Onboard new trading partners in under 24 hours with guided mapping wizards and automated testing workflows.",
      },
      {
        title: "Compliance & Audit Readiness",
        description:
          "Tamper-proof audit trail for every transaction with timestamps, attribution, and before/after document snapshots.",
      },
      {
        title: "Cross-Partner Exception Intelligence",
        description:
          "Surface systemic failure patterns across your partner network and quantify the downstream cost of each failure category.",
      },
    ],
    integrations: [
      "AS2",
      "SFTP/FTPS",
      "APIs & Webhooks",
      "VAN Integrations",
      "SAP",
      "Oracle",
    ],
    faq: [
      {
        question: "Which EDI standards are supported?",
        answer:
          "EDIFAI supports X12, EDIFACT, XML, JSON, and CSV formats with extensible transformation rules and AI-assisted mapping.",
      },
      {
        question: "How does EDIFAI differ from traditional EDI?",
        answer:
          "Traditional EDI reacts after failures. EDIFAI anticipates issues before they disrupt, corrects errors automatically, and learns from recurring patterns — shifting from reactive to predictive.",
      },
    ],
    getStarted: {
      badge: "EDI Transformation",
      headline: "The Shift from Reactive to Predictive",
      subtitle:
        "Traditional EDI breaks and waits. EDIFAI anticipates and prevents.",
      steps: [
        {
          num: "Before",
          title: "Transaction Fails, Ticket Opens",
          desc: "A malformed 856 hits your system at 2 AM. Nobody notices until the warehouse can't process the ASN. Manual investigation begins — hours lost, shipments delayed.",
        },
        {
          num: "The Shift",
          title: "AI Validates at the Gate",
          desc: "Every inbound document scores for risk before processing. Schema violations, missing segments, and anomalies are caught — and most are auto-corrected — instantly.",
        },
        {
          num: "After",
          title: "Continuous Intelligence, Zero Tickets",
          desc: "Self-learning correction rules handle recurring errors. Partner scorecards quantify document quality. Your EDI team manages strategy, not firefighting.",
        },
      ],
    },
    whyChoose: {
      headline: "Why Modern EDI Demands Intelligence",
      subtitle: "EDI failures cascade into missed shipments and chargebacks.",
      items: [
        {
          title: "Pre-Processing Risk Detection",
          desc: "Every inbound document is scored for risk before entering your systems — schema violations, missing fields, and anomalies are caught at the gate.",
          variant: "accent",
        },
        {
          title: "Self-Learning Error Correction",
          desc: "Learns from every manual fix and proposes auto-correction rules — transforming tribal knowledge into scalable automated intelligence.",
          variant: "teal",
        },
        {
          title: "Business-Friendly Visibility",
          desc: "Transaction status in plain language, not EDI segment codes. Role-specific dashboards for procurement, logistics, and finance.",
          variant: "accent",
        },
        {
          title: "Partner Performance Scoring",
          desc: "Track which partners send clean documents and which generate exceptions — quantified leverage for SLA renegotiations.",
          variant: "teal",
        },
      ],
    },
    platformHighlight: {
      headline: "Your EDI Intelligence Layer",
      subheadline: "Predictive, Not Reactive",
      description:
        "A single platform to monitor, validate, correct, and report on every B2B transaction — with AI that learns and improves continuously.",
      bullets: [
        "Real-time transaction dashboard with risk scores and document-level drill-down",
        "Auto-correction audit trail with before/after comparison and confidence scoring",
        "Partner onboarding wizard with go-live certification in under 24 hours",
        "Exception trend analytics across partners, document types, and time windows",
      ],
      stats: [
        {
          value: "65%",
          label: "Faster Resolution",
          variant: "accent",
        },
        {
          value: "50%",
          label: "Fewer Failures",
          variant: "teal",
        },
        {
          value: "70%",
          label: "Less Manual Work",
          variant: "accent",
        },
        {
          value: "< 1d",
          label: "Partner Onboarding",
          variant: "teal",
        },
      ],
    },
  },
  {
    slug: "ai-rule-engine",
    heading: "AI Rule Engine",
    tagline:
      "Define logistics rules in plain language. Let AI execute them autonomously.",
    overViewTagLine: "Turn Business Intent Into Automated Action",
    overViewDesc:
      "Create rules like If shipment delay exceeds 4 hours, notify customer and reschedule delivery without writing code. The AI Rule Engine interprets intent, connects to relevant systems, and executes actions in real time.",
    overViewOpints: [
      "Chat-based rule configuration using natural language",
      "Automated reroute triggers based on real-time conditions",
      "SLA-based escalation with multi-tier notification chains",
    ],
    capabilities: [
      "Chat-based rule configuration using natural language",
      "Automated reroute triggers based on real-time conditions",
      "SLA-based escalation with multi-tier notification chains",
      "No-code workflow orchestration with drag-and-drop builder",
      "Conditional logic branching for complex business scenarios",
      "Event-driven automation with system-wide trigger support",
      "Rule versioning, testing, and rollback capabilities",
      "Integration with email, SMS, and webhook notification channels",
    ],
    impact: [
      "Reduced manual oversight and intervention by up to 70%",
      "Faster disruption response (minutes instead of hours)",
      "Improved policy consistency across all operations",
      "Lower operational costs through autonomous decision-making",
      "Reduced human error in exception handling workflows",
      "Scalable operations without proportional headcount growth",
    ],
    metrics: [
      {
        value: "80%",
        label: "Reduction in Manual Oversight",
      },
      {
        value: "10x",
        label: "Faster Rule Deployment",
      },
      {
        value: "45%",
        label: "Faster Disruption Response",
      },
      {
        value: "Zero",
        label: "Code Required",
      },
    ],
    useCases: [
      {
        title: "Automated Exception Handling",
        description:
          "Define rules like 'If shipment delayed > 4 hours, reroute via backup carrier and notify customer.'",
      },
      {
        title: "SLA-Based Escalation",
        description:
          "Automatically escalate issues based on priority, customer tier, and service level agreements.",
      },
      {
        title: "Dynamic Pricing Rules",
        description:
          "Implement rate adjustments based on demand, capacity, and market conditions without developer involvement.",
      },
    ],
    integrations: [
      "Slack",
      "Microsoft Teams",
      "Email",
      "Webhooks",
      "Any REST API",
    ],
    faq: [
      {
        question: "Do I need technical skills to create rules?",
        answer:
          "No. Rules are defined in natural language through a chat interface — no coding or scripting required.",
      },
      {
        question: "How quickly do rules take effect?",
        answer:
          "Rules are active within seconds of creation and begin monitoring immediately.",
      },
    ],
    getStarted: {
      badge: "Living Automation",
      headline: "Rules That Think, Learn, and Adapt",
      subtitle:
        "Not static workflows — intelligent rules that evolve with your operations.",
      steps: [
        {
          num: "Create",
          title: "Natural Language Rule Design",
          desc: "Type 'If shipment delayed > 4 hours on premium lane, switch to air and notify the account manager.' AI parses, validates, and builds executable logic instantly.",
        },
        {
          num: "Validate",
          title: "Historical Backtesting",
          desc: "Every rule simulates against 90 days of real events before deployment — showing trigger frequency, false positive rate, and projected cost impact.",
        },
        {
          num: "Evolve",
          title: "Performance-Driven Refinement",
          desc: "Live dashboards track which rules fire, which prevent losses, and which generate noise. Refine or rollback with version control — no downtime, no risk.",
        },
      ],
    },
    whyChoose: {
      headline: "End Manual Exception Handling",
      subtitle: "Stop losing hours to escalation emails and firefighting.",
      items: [
        {
          title: "Natural Language Configuration",
          desc: "No code, no flowcharts, no developer tickets. Type your business rule in plain English and the AI translates it into executable logic — tested and deployed in minutes.",
          variant: "accent",
        },
        {
          title: "Backtesting Before Deployment",
          desc: "Every rule runs against 90 days of historical events before going live — showing you exactly what would have triggered, what actions would have fired, and the financial impact.",
          variant: "teal",
        },
        {
          title: "Multi-System Orchestration",
          desc: "A single rule can trigger actions across TMS, WMS, CRM, and notification channels simultaneously — no middleware, no integration projects, no delays.",
          variant: "accent",
        },
        {
          title: "Rule Versioning & Rollback",
          desc: "Every rule change is version-tracked. If a new rule causes unexpected triggers, roll back to the previous version with one click — no downtime, no data loss.",
          variant: "teal",
        },
      ],
    },
    platformHighlight: {
      headline: "Autonomous Operations, Zero Complexity",
      subheadline: "Rules That Think",
      description:
        "AI Rule Engine gives operations managers, logistics directors, and customer success teams the power to encode their expertise into living automation — rules that monitor, decide, and act 24/7.",
      bullets: [
        "Chat-based rule builder with real-time syntax validation and preview",
        "Event timeline showing every trigger, action, and outcome with full context",
        "Rule performance dashboard tracking trigger frequency, false positives, and business impact",
        "Team collaboration with shared rule libraries, approval workflows, and change history",
      ],
      stats: [
        {
          value: "80%",
          label: "Less Oversight",
          variant: "accent",
        },
        {
          value: "10x",
          label: "Faster Deploy",
          variant: "teal",
        },
        {
          value: "< 5min",
          label: "Rule Creation",
          variant: "accent",
        },
        {
          value: "Zero",
          label: "Code Needed",
          variant: "teal",
        },
      ],
    },
  },
  {
    slug: "document-compliance",
    heading: "Document & Compliance Automation",
    tagline:
      "Eliminate paperwork bottlenecks with AI-powered document intelligence.",
    overViewTagLine: "Reduce Regulatory Risk and Processing Errors",
    overViewDesc:
      "Automatically validate documents against predefined trade and compliance rules before submission. Prevent costly delays, rejections, and audit penalties caused by incomplete or inaccurate paperwork.",
    overViewOpints: [
      "AI-powered document extraction with OCR and NLP",
      "Customs validation and HS code classification",
      "Compliance dashboards with real-time risk indicators",
    ],
    capabilities: [
      "AI-powered document extraction with OCR and NLP",
      "Customs validation and HS code classification",
      "Compliance dashboards with real-time risk indicators",
      "Audit tracking with full document lineage and history",
      "Automated document matching (PO, invoice, BOL)",
      "Regulatory change monitoring and alert notifications",
      "Multi-country trade compliance rule management",
      "Digital document archiving with instant retrieval",
    ],
    impact: [
      "Reduced document processing time by up to 80%",
      "Fewer compliance delays and costly shipment holds",
      "Improved audit readiness with complete documentation trails",
      "Lower risk of regulatory fines and penalties",
      "Reduced manual data entry errors and rework",
      "Faster customs clearance and border processing",
    ],
    metrics: [
      {
        value: "90%",
        label: "Faster Document Processing",
      },
      {
        value: "75%",
        label: "Fewer Compliance Delays",
      },
      {
        value: "100%",
        label: "Audit Trail Coverage",
      },
      {
        value: "50%",
        label: "Cost Reduction in Processing",
      },
    ],
    useCases: [
      {
        title: "Customs Document Automation",
        description:
          "Auto-extract and validate bills of lading, commercial invoices, and packing lists for customs clearance.",
      },
      {
        title: "Regulatory Compliance Tracking",
        description:
          "Monitor FDA, USDA, and EPA compliance requirements with automated alerts and documentation.",
      },
      {
        title: "Audit Preparation",
        description:
          "Generate comprehensive audit packages with complete document trails and compliance verification.",
      },
    ],
    integrations: [
      "SAP GTS",
      "Oracle GTM",
      "Customs brokers",
      "FDA PREDICT",
      "ACE",
    ],
    faq: [
      {
        question: "What document types are supported?",
        answer:
          "BOLs, commercial invoices, packing lists, certificates of origin, FDA forms, and 50+ additional document types.",
      },
      {
        question: "How accurate is the AI extraction?",
        answer:
          "Our models achieve 97%+ accuracy on structured documents and 92%+ on semi-structured formats.",
      },
    ],
    getStarted: {
      badge: "Regulatory Shield",
      headline: "Three Layers of Compliance Protection",
      subtitle:
        "Capture, validate, and archive — every document audit-ready before it's needed.",
      steps: [
        {
          num: "Capture",
          title: "Intelligent Document Ingestion",
          desc: "BOLs, invoices, certificates, and customs forms flow in via drag-and-drop, email watchers, or API feeds — OCR extracts every critical field automatically.",
        },
        {
          num: "Validate",
          title: "Cross-Document Matching Engine",
          desc: "AI cross-references quantities, values, and HS codes across BOL-invoice-packing list triads — flagging discrepancies before customs submission.",
        },
        {
          num: "Protect",
          title: "Tamper-Proof Audit Archive",
          desc: "Every version, approval, and modification carries timestamps and attribution. Auditors get a complete chain of custody — exportable in one click.",
        },
      ],
    },
    whyChoose: {
      headline: "End Manual Document Processing",
      subtitle:
        "Stop drowning in document verification and regulatory deadlines.",
      items: [
        {
          title: "Three-Way Document Matching",
          desc: "Automatically cross-reference BOL, commercial invoice, and packing list — flagging quantity mismatches, value discrepancies, and missing fields before customs submission.",
          variant: "accent",
        },
        {
          title: "HS Code Classification Engine",
          desc: "AI suggests harmonized system codes based on product descriptions, historical classifications, and ruling databases — reducing misclassification risk and duty overpayment.",
          variant: "teal",
        },
        {
          title: "Regulatory Change Monitoring",
          desc: "Track FDA, USDA, EPA, and CBP regulatory updates in real time. When rules change, affected shipments are flagged automatically — preventing holds at the border.",
          variant: "accent",
        },
        {
          title: "Complete Audit Lineage",
          desc: "Every document version, approval, and modification is timestamped and attributed — giving auditors a complete chain of custody without scrambling through email archives.",
          variant: "teal",
        },
      ],
    },
    platformHighlight: {
      headline: "Shipment Compliance Intelligence",
      subheadline: "Zero Gaps, Zero Holds",
      description:
        "Document & Compliance Automation gives compliance officers, trade managers, and customs teams a single platform to ingest, validate, match, and archive every trade document — with AI catching discrepancies humans miss.",
      bullets: [
        "Document matching dashboard showing BOL-invoice-packing list alignment per shipment",
        "Compliance risk heatmap highlighting shipments with regulatory exposure",
        "Automated HS code suggestions with confidence scores and ruling references",
        "Audit package generator producing ready-to-submit documentation bundles",
      ],
      stats: [
        {
          value: "90%",
          label: "Faster Processing",
          variant: "accent",
        },
        {
          value: "97%+",
          label: "Extraction Accuracy",
          variant: "teal",
        },
        {
          value: "75%",
          label: "Fewer Delays",
          variant: "accent",
        },
        {
          value: "100%",
          label: "Audit Coverage",
          variant: "teal",
        },
      ],
    },
  },
  {
    slug: "po-pl-tracker",
    heading: "PO & P&L Tracker",
    tagline:
      "See the true profitability of every order, shipment, and customer.",
    overViewTagLine: "Make Margin-Based Decisions With Confidence",
    overViewDesc:
      "Track revenue, freight costs, duties, storage, and operational expenses against each order in real time. Identify low-margin shipments early and take corrective action before profitability erodes.",
    overViewOpints: [
      "PO lifecycle tracking from creation to payment",
      "Freight, duty, and accessorial cost allocation",
      "Margin dashboards with drill-down by product and lane",
    ],
    capabilities: [
      "PO lifecycle tracking from creation to payment",
      "Freight, duty, and accessorial cost allocation",
      "Margin dashboards with drill-down by product and lane",
      "ERP integration with automated data synchronization",
      "Variance analysis between estimated and actual costs",
      "Multi-currency support with real-time exchange rates",
      "Cost allocation rules engine for complex supply chains",
      "Profitability trending and forecasting analytics",
    ],
    impact: [
      "Improved margin transparency across all operations",
      "Reduced cost leakage through automated reconciliation",
      "Faster financial reconciliation (days instead of weeks)",
      "Better pricing decisions with true landed cost visibility",
      "Reduced billing disputes with automated cost validation",
      "Enhanced financial planning and budgeting accuracy",
    ],
    metrics: [
      {
        value: "35%",
        label: "Improved Margin Transparency",
      },
      {
        value: "50%",
        label: "Faster Financial Reconciliation",
      },
      {
        value: "20%",
        label: "Reduction in Cost Leakage",
      },
      {
        value: "Real-time",
        label: "P&L Visibility",
      },
    ],
    useCases: [
      {
        title: "Order-Level Profitability",
        description:
          "Track actual costs against estimates at the individual order level including freight, duties, and handling.",
      },
      {
        title: "Customer P&L Analysis",
        description:
          "Understand true profitability by customer segment with automated cost allocation and revenue attribution.",
      },
      {
        title: "Variance Alerting",
        description:
          "Get notified instantly when actual costs deviate from planned margins beyond configurable thresholds.",
      },
    ],
    integrations: [
      "SAP FI",
      "Oracle Financials",
      "QuickBooks",
      "NetSuite",
      "Xero",
    ],
    faq: [
      {
        question: "How granular is the cost tracking?",
        answer:
          "Down to the individual line item level, with automated allocation of shared costs like freight and handling.",
      },
      {
        question: "Does it integrate with our ERP?",
        answer:
          "Yes, with pre-built connectors for all major ERP systems and custom API integration support.",
      },
    ],
    getStarted: {
      badge: "Margin Anatomy",
      headline: "Where Your Profitability Actually Lives",
      subtitle:
        "Most companies don't know their true order-level margins. Here's how we fix that.",
      steps: [
        {
          num: "Revenue",
          title: "Order-Level Revenue Attribution",
          desc: "Every line item, surcharge, and discount maps to the specific order it belongs to — creating a clean revenue baseline that finance can trust.",
        },
        {
          num: "Cost",
          title: "Granular Cost Allocation",
          desc: "Freight charges, duty payments, accessorials, and handling fees allocate to the orders that caused them — no more averaging costs across the portfolio.",
        },
        {
          num: "Variance",
          title: "Real-Time Margin Monitoring",
          desc: "When actual costs exceed planned margins by your threshold, alerts fire immediately — not 30 days later during the accounting close.",
        },
      ],
    },
    whyChoose: {
      headline: "Expose Hidden Cost Leakage",
      subtitle: "Your true margins are likely lower than reported.",
      items: [
        {
          title: "Order-Level Cost Truth",
          desc: "Every freight charge, duty payment, accessorial fee, and handling cost is allocated to the specific order that caused it — no more averaging costs across shipments.",
          variant: "accent",
        },
        {
          title: "Real-Time Variance Detection",
          desc: "When actual costs exceed estimated margins by your threshold (5%, 10%, custom), alerts fire immediately — not 30 days later in the accounting close.",
          variant: "teal",
        },
        {
          title: "Customer Profitability Ranking",
          desc: "See which customers generate margin and which destroy it — accounting for freight complexity, return rates, accessorial charges, and payment terms.",
          variant: "accent",
        },
        {
          title: "Multi-Currency Reconciliation",
          desc: "Handle USD, EUR, GBP, and 50+ currencies with real-time exchange rate application and gain/loss tracking per transaction — eliminating manual FX adjustments.",
          variant: "teal",
        },
      ],
    },
    platformHighlight: {
      headline: "Shipment Financial Intelligence",
      subheadline: "Margin Clarity in Real Time",
      description:
        "PO & P&L Tracker gives finance teams, logistics controllers, and executive leadership real-time visibility into the true cost and profitability of every order — before the accounting close reveals surprises.",
      bullets: [
        "Order-level P&L waterfall charts showing revenue, COGS, freight, duty, and margin",
        "Customer profitability league table ranked by true contribution margin",
        "Variance alert dashboard with drill-down into cost overruns by category",
        "Month-over-month margin trending with forecast vs. actual comparison",
      ],
      stats: [
        {
          value: "35%",
          label: "More Transparency",
          variant: "accent",
        },
        {
          value: "50%",
          label: "Faster Recon",
          variant: "teal",
        },
        {
          value: "20%",
          label: "Less Leakage",
          variant: "accent",
        },
        {
          value: "Real-time",
          label: "P&L Updates",
          variant: "teal",
        },
      ],
    },
  },
  {
    slug: "unified-visibility",
    heading: "Unified Visibility & Insights",
    tagline: "One screen. Every system. Complete operational clarity.",
    overViewTagLine: "Make Faster, Data-Backed Decisions",
    overViewDesc:
      "Consolidate operational data across planning, warehouse, transportation, and finance into a single decision layer. Eliminate delays caused by siloed reporting and manual data reconciliation.",
    overViewOpints: [
      "Cross-system dashboards with real-time data aggregation",
      "Delay heatmaps and bottleneck identification tools",
      "Landed cost analytics with component-level breakdown",
    ],
    capabilities: [
      "Cross-system dashboards with real-time data aggregation",
      "Delay heatmaps and bottleneck identification tools",
      "Landed cost analytics with component-level breakdown",
      "Carrier benchmarking and performance league tables",
      "Custom report builder with scheduled distribution",
      "Predictive analytics for proactive decision-making",
      "Role-based dashboard personalization and access control",
      "Data export and API access for external BI integration",
    ],
    impact: [
      "Eliminates siloed reporting and fragmented visibility",
      "Faster executive decision-making with real-time insights",
      "Improved planning alignment across departments",
      "Reduced time spent on manual report generation by 60%",
      "Better supplier and carrier accountability through data",
      "Enhanced strategic planning with trend analytics",
    ],
    metrics: [
      {
        value: "100%",
        label: "Cross-System Visibility",
      },
      {
        value: "70%",
        label: "Faster Decision-Making",
      },
      {
        value: "5 min",
        label: "Average Report Generation",
      },
      {
        value: "Zero",
        label: "Siloed Reports",
      },
    ],
    useCases: [
      {
        title: "Executive Command Center",
        description:
          "Give leadership a single dashboard spanning procurement, logistics, warehousing, and finance.",
      },
      {
        title: "Carrier Performance Benchmarking",
        description:
          "Compare carrier performance across cost, reliability, and service quality metrics in real time.",
      },
      {
        title: "Landed Cost Analytics",
        description:
          "Break down total landed costs by component with drill-down into freight, duties, handling, and insurance.",
      },
    ],
    integrations: ["SAP", "Oracle", "Tableau", "Power BI", "Custom APIs"],
    faq: [
      {
        question: "How long does integration take?",
        answer:
          "Standard ERP integrations are live within 2 weeks. Custom integrations typically take 3–4 weeks.",
      },
      {
        question: "Can I create custom dashboards?",
        answer:
          "Yes, with drag-and-drop dashboard builder and 100+ pre-built visualization widgets.",
      },
    ],
    getStarted: {
      badge: "Signal Unification",
      headline: "From Five Reports to One Truth",
      subtitle:
        "When every department runs different numbers, nobody trusts any of them.",
      steps: [
        {
          num: "Unify",
          title: "Cross-System Normalization",
          desc: "ERP, TMS, WMS, and financial feeds merge into one normalized layer — conflicting metrics reconcile automatically so every team sees identical numbers.",
        },
        {
          num: "Visualize",
          title: "Drag-and-Drop Dashboards",
          desc: "100+ pre-built widgets. Analysts build their own views without BI team bottlenecks — from executive summaries to lane-level deep dives.",
        },
        {
          num: "Distribute",
          title: "Automated Stakeholder Reports",
          desc: "Scheduled PDF and Excel exports with role-based access. Leadership gets trend summaries. Analysts get drill-downs. Nobody waits for a report request queue.",
        },
      ],
    },
    whyChoose: {
      headline: "End the Reporting Scramble",
      subtitle: "Five teams, five metrics — none of them match. Until now.",
      items: [
        {
          title: "Cross-System Data Unification",
          desc: "Pull from ERP, TMS, WMS, and financial systems into one normalized data layer — no more reconciling conflicting numbers from different departments.",
          variant: "accent",
        },
        {
          title: "Delay & Bottleneck Heatmaps",
          desc: "Visualize where shipments stall, which lanes underperform, and which carriers miss deadlines — with geographic and temporal drill-down to root causes.",
          variant: "teal",
        },
        {
          title: "Self-Serve Report Builder",
          desc: "Empower analysts and managers to build their own reports with drag-and-drop widgets — reducing dependence on BI teams and eliminating 2-week report request queues.",
          variant: "accent",
        },
        {
          title: "Predictive Trend Alerts",
          desc: "The platform doesn't just show what happened — it flags emerging trends (rising transit times, deteriorating carrier scores) before they become crises.",
          variant: "teal",
        },
      ],
    },
    platformHighlight: {
      headline: "Your Entire Supply Chain, One Dashboard",
      subheadline: "See Everything, Decide Faster",
      description:
        "Unified Visibility & Insights gives executives, analysts, and operations managers a single analytics hub that spans every system, every partner, and every metric — turning fragmented reports into actionable intelligence.",
      bullets: [
        "Executive command center with cross-functional KPI tiles and trend arrows",
        "Carrier performance league tables with cost, reliability, and service rankings",
        "Landed cost waterfall charts with component-level drill-down per shipment",
        "Scheduled report automation with PDF/Excel export and email distribution",
      ],
      stats: [
        {
          value: "100%",
          label: "System Coverage",
          variant: "accent",
        },
        {
          value: "70%",
          label: "Faster Decisions",
          variant: "teal",
        },
        {
          value: "5min",
          label: "Report Speed",
          variant: "accent",
        },
        {
          value: "Zero",
          label: "Data Silos",
          variant: "teal",
        },
      ],
    },
  },
  {
    slug: "calio",
    tagline:
      "Scoring that strengthens the modern supply chain — from insight to impact across the ecosystem.",
    overViewTagLine: "Turn Performance Signals Into Strategic Direction",
    overViewDesc:
      "CALIO aggregates operational, financial, and service metrics into a unified scoring model that highlights strengths, gaps, and risk exposure. Leadership teams gain a clear view of where to invest, where to intervene, and where performance is deteriorating.",
    overViewOpints: [
      "150+ purpose-designed metrics across cost, service, speed, and resilience",
      "Demand & supply alignment scoring — forecast accuracy, plan stability, responsiveness",
      "Inventory health & flow efficiency — velocity, balance, positioning, working capital",
    ],
    capabilities: [
      "150+ purpose-designed metrics across cost, service, speed, and resilience",
      "Demand & supply alignment scoring — forecast accuracy, plan stability, responsiveness",
      "Inventory health & flow efficiency — velocity, balance, positioning, working capital",
      "Logistics execution & freight performance — reliability, cost efficiency, variability",
      "Partner reliability & network performance — consistency, collaboration maturity",
      "System integration & data flow readiness — timeliness, completeness, quality",
      "Prescriptive guidance with outcome-focused recommendations",
      "Continuous scoring recalibration as operations evolve",
    ],
    impact: [
      "Single trusted performance baseline across the entire ecosystem",
      "Clear priority direction — capabilities activated where value is highest",
      "Outcome measurement with continuous improvement tracking",
      "Solution-agnostic and flexible improvement paths",
      "Objective recommendations with no vendor lock-in",
      "Accelerated transformation from insight to strategic execution",
    ],
    metrics: [
      {
        value: "150+",
        label: "Supply Chain Metrics",
      },
      {
        value: "50+",
        label: "Active Customers",
      },
      {
        value: "30+",
        label: "Use Cases",
      },
      {
        value: "End-to-End",
        label: "Ecosystem Coverage",
      },
    ],
    useCases: [
      {
        title: "Calibrated Performance Scoring",
        description:
          "Create a unified performance score across systems, processes, partners, and physical flows to establish clarity and a trusted baseline.",
      },
      {
        title: "Prescriptive Priority Direction",
        description:
          "Translate complexity into insight by identifying areas of strength, opportunities for elevation, and high-impact improvement paths aligned to business objectives.",
      },
      {
        title: "Outcome-Driven Execution",
        description:
          "Transform performance scores into clear, recommended execution paths with prescriptive guidance across planning, visibility, integration, and intelligence.",
      },
    ],
    integrations: [
      "ERP Systems",
      "TMS Platforms",
      "WMS Systems",
      "BI Tools",
      "Custom Sources",
    ],
    faq: [
      {
        question: "How does CALIO differ from traditional assessments?",
        answer:
          "CALIO moves beyond static assessments toward structured, outcome-driven transformation — converting complex operational signals into calibrated performance insight with 150+ purpose-designed metrics.",
      },
      {
        question: "Is CALIO solution-agnostic?",
        answer:
          "Yes, CALIO maintains objective, outcome-focused recommendations with no vendor lock-in. It extends beyond KGS Technology's native capabilities through a trusted partner ecosystem.",
      },
    ],
    getStarted: {
      badge: "Performance Framework",
      headline: "The Three Pillars of CALIO",
      subtitle:
        "Calibrate where you stand. Prioritize where to invest. Transform how you operate.",
      steps: [
        {
          num: "Calibrate",
          title: "Establish Your Ecosystem Baseline",
          desc: "150+ purpose-built supply chain metrics score your operations across cost efficiency, service quality, speed, and resilience — objectively and continuously.",
        },
        {
          num: "Prioritize",
          title: "Rank Improvement Opportunities",
          desc: "Every score comes with specific recommended actions ranked by business impact vs. implementation effort — no more debating where to start.",
        },
        {
          num: "Transform",
          title: "Execute with Prescriptive Guidance",
          desc: "Quarterly milestone tracking with vendor-agnostic recommendations from KGS Technology and its partner ecosystem. Progress rescores automatically as operations evolve.",
        },
      ],
    },
    whyChoose: {
      headline: "Replace Ad-Hoc Assessments",
      subtitle: "Objective, structured insight for C-suite leaders.",
      items: [
        {
          title: "150+ Purpose-Designed Metrics",
          desc: "Not generic KPIs. CALIO's metrics are purpose-built for supply chain — covering demand alignment, inventory health, logistics execution, partner reliability, and system readiness.",
          variant: "accent",
        },
        {
          title: "Vendor-Agnostic Recommendations",
          desc: "CALIO scores your operations objectively and recommends solutions from KGS Technology and its partner ecosystem — no lock-in, no bias toward any single vendor's product.",
          variant: "teal",
        },
        {
          title: "Continuous Recalibration",
          desc: "Unlike one-time assessments that gather dust, CALIO continuously rescores as your operations evolve — keeping leadership aligned on progress and shifting priorities.",
          variant: "accent",
        },
        {
          title: "Prescriptive, Not Just Descriptive",
          desc: "Every score comes with specific recommended actions ranked by impact and difficulty — transforming abstract performance gaps into concrete execution priorities.",
          variant: "teal",
        },
      ],
    },
    platformHighlight: {
      headline: "Strategic Supply Chain Compass",
      subheadline: "Score, Prioritize, Transform",
      description:
        "CALIO gives executive leadership, supply chain directors, and transformation offices a calibrated, continuously updated view of ecosystem performance — with prescriptive pathways that turn scores into strategic action.",
      bullets: [
        "Ecosystem health dashboard with dimension scores for cost, service, speed, and resilience",
        "Improvement priority matrix ranking opportunities by business impact vs. implementation effort",
        "Benchmark comparisons against industry peers and best-in-class performers",
        "Transformation roadmap generator with quarterly milestone tracking",
      ],
      stats: [
        {
          value: "150+",
          label: "Metrics Scored",
          variant: "accent",
        },
        {
          value: "End-to-End",
          label: "Coverage",
          variant: "teal",
        },
        {
          value: "50+",
          label: "Customers",
          variant: "accent",
        },
        {
          value: "Continuous",
          label: "Recalibration",
          variant: "teal",
        },
      ],
    },
  },
  {
    slug: "iqua",
    heading: "IQUA",
    tagline: "One platform. One operational view. Complete shipment clarity.",
    overViewTagLine: "Proactively Manage Shipment Exceptions",
    overViewDesc:
      "Identify missing documents, clearance risks, and schedule changes instantly. Replace reactive follow-ups with structured workflows that keep imports moving without last-minute surprises.",
    overViewOpints: [
      "Unified real-time visibility across airlines, ports, vendors, and customers",
      "Continuity across split shipments with consolidated shipment views",
      "Predictive ETA awareness with real-time change tracking",
    ],
    capabilities: [
      "Unified real-time visibility across airlines, ports, vendors, and customers",
      "Continuity across split shipments with consolidated shipment views",
      "Predictive ETA awareness with real-time change tracking",
      "Context-aware prioritization based on timing, delays, and impact",
      "Automated notifications for arrivals, delays, and required actions",
      "Compliance & documentation tracking with release readiness monitoring",
      "Shelf-life and expiration management with FEFO optimization",
      "Multi-echelon inventory optimization across supply tiers",
    ],
    impact: [
      "Protect product quality with real-time visibility and faster action on temperature-sensitive shipments",
      "Improve cost efficiency by reducing dwell time, storage costs, and avoidable penalties",
      "Reduce manual effort by replacing multiple portals, spreadsheets, and email follow-ups",
      "Strengthen coordination across importers, brokers, vendors, and warehouses",
      "Enhance operational control with a single dashboard for all shipments and exceptions",
      "Prevent delays with early visibility into document readiness and release status",
    ],
    metrics: [
      {
        value: "Real-time",
        label: "Shipment Visibility",
      },
      {
        value: "Air & Ocean",
        label: "Unified Tracking",
      },
      {
        value: "10+",
        label: "Customers",
      },
      {
        value: "100%",
        label: "Document Readiness",
      },
    ],
    useCases: [
      {
        title: "Fresh Seafood Air Freight",
        description:
          "Continuously monitor flight schedules, arrivals, and pickup windows for high-velocity fresh shipments with partial arrival awareness.",
      },
      {
        title: "Frozen Seafood Ocean & Multimodal",
        description:
          "Track container movement, ETA changes, and release readiness across longer routes with customs and FDA milestone monitoring.",
      },
      {
        title: "Document & Release Readiness",
        description:
          "Get early visibility into document readiness and release status to resolve issues before shipments arrive.",
      },
    ],
    integrations: [
      "Airlines",
      "Ocean Carriers",
      "Customs",
      "FDA",
      "Freight Forwarders",
    ],
    faq: [
      {
        question: "Does IQUA support both air and ocean shipments?",
        answer:
          "Yes, IQUA consolidates AWB-level air and container-level ocean shipment visibility into a single platform.",
      },
      {
        question: "Can it handle split shipments?",
        answer:
          "Yes, when shipments move across multiple flights, each movement remains connected within one consolidated view for seamless tracking.",
      },
    ],
    getStarted: {
      badge: "Time-Critical Visibility",
      headline: "When Hours Define Product Quality",
      subtitle:
        "For perishable imports, delayed visibility means spoiled cargo and rejected loads.",
      steps: [
        {
          num: "Origin",
          title: "Departure & Flight Monitoring",
          desc: "Track flight schedules, vessel departures, and routing changes from the moment cargo leaves the supplier — partial shipment awareness included.",
        },
        {
          num: "In Transit",
          title: "ETA Trend Intelligence",
          desc: "Not just the latest ETA — IQUA shows whether arrival times are improving, deteriorating, or stable, so planners adjust receiving schedules proactively.",
        },
        {
          num: "Arrival",
          title: "Release & Pickup Readiness",
          desc: "FDA hold/release status, customs clearance progression, and terminal pickup windows surface in real time — preventing cold chain breaks from unattended cargo.",
        },
      ],
    },
    whyChoose: {
      headline: "Built for Cold Chain Operations",
      subtitle: "Hours of delay mean spoiled product and rejected loads.",
      items: [
        {
          title: "Split Shipment Tracking",
          desc: "When one PO moves across three flights and two containers, IQUA keeps every piece connected in a single view — no manual reconciliation of partial arrivals.",
          variant: "accent",
        },
        {
          title: "FDA & Customs Milestone Monitoring",
          desc: "Track Prior Notice filing, FDA hold/release status, and customs clearance progression in real time — catching compliance gaps before the cargo arrives at port.",
          variant: "teal",
        },
        {
          title: "Pickup Window Awareness",
          desc: "Know exactly when air shipments are ready for pickup at the terminal — preventing cold chain breaks caused by cargo sitting unattended past safe holding times.",
          variant: "accent",
        },
        {
          title: "ETA Change Intelligence",
          desc: "IQUA doesn't just show the latest ETA — it shows the ETA trend (improving, deteriorating, stable) so planners adjust warehouse receiving schedules proactively.",
          variant: "teal",
        },
      ],
    },
    platformHighlight: {
      headline: "Time-Critical Import Intelligence",
      subheadline: "Every Shipment, Every Milestone",
      description:
        "IQUA gives import coordinators, logistics managers, and quality teams a single live view of every air and ocean shipment — with proactive alerts that protect product quality and customer commitments.",
      bullets: [
        "Unified air-ocean dashboard showing all active shipments with milestone status",
        "ETA trend indicators showing improvement or deterioration per shipment",
        "Document readiness tracker with FDA, customs, and broker status per consignment",
        "Exception priority queue ranked by product perishability and customer SLA urgency",
      ],
      stats: [
        {
          value: "Real-time",
          label: "Visibility",
          variant: "accent",
        },
        {
          value: "Air+Ocean",
          label: "Unified View",
          variant: "teal",
        },
        {
          value: "100%",
          label: "Doc Readiness",
          variant: "accent",
        },
        {
          value: "10+",
          label: "Customers",
          variant: "teal",
        },
      ],
    },
  },
  {
    slug: "customer-portal",
    heading: "Customer Portal for 3PL",
    tagline:
      "Give your customers the transparency they demand — branded and secure.",
    overViewTagLine: "Reduce Customer Support Overload",
    overViewDesc:
      "Provide customers with real-time shipment status, inventory levels, and document access without relying on manual updates. Lower inbound queries and free up operations teams to focus on execution.",
    overViewOpints: [
      "Real-time shipment and inventory visibility for customers",
      "Issue tracking and resolution with ticket management",
      "Role-based access control with multi-tenant support",
    ],
    capabilities: [
      "Real-time shipment and inventory visibility for customers",
      "Issue tracking and resolution with ticket management",
      "Role-based access control with multi-tenant support",
      "Document repository with secure sharing and downloads",
      "Custom branding and white-label configuration options",
      "Automated status notifications and digest emails",
      "Self-service reporting and analytics dashboards",
      " API access for customer system integration",
    ],
    impact: [
      "Reduced support tickets and customer inquiries by 40%",
      "Improved customer satisfaction and NPS scores",
      "Greater operational transparency and trust building",
      "Lower customer service costs through self-service",
      "Faster issue resolution with structured tracking",
      "Enhanced customer retention through premium experience",
    ],
    metrics: [
      {
        value: "60%",
        label: "Fewer Support Tickets",
      },
      {
        value: "4.8/5",
        label: "Customer Satisfaction Score",
      },
      {
        value: "24/7",
        label: "Self-Service Access",
      },
      {
        value: "100%",
        label: "White-Label Ready",
      },
    ],
    useCases: [
      {
        title: "Shipment Self-Service",
        description:
          "Let customers track shipments, view ETAs, and download PODs without contacting your support team.",
      },
      {
        title: "Inventory Visibility",
        description:
          "Give customers real-time visibility into their inventory levels, aging, and location across your network.",
      },
      {
        title: "Document Access",
        description:
          "Provide a secure, organized repository for invoices, BOLs, customs docs, and compliance records.",
      },
    ],
    integrations: [
      "Salesforce",
      "HubSpot",
      "Zendesk",
      "Freshdesk",
      "Custom SSO",
    ],
    faq: [
      {
        question: "Can we customize the branding?",
        answer:
          "Fully white-labeled with your logo, colors, domain, and custom styling — your customers see your brand.",
      },
      {
        question: "What access controls are available?",
        answer:
          "Role-based access with granular permissions at the user, team, and organization level.",
      },
    ],
    getStarted: {
      badge: "Customer Experience",
      headline: "What Your Customers Actually See",
      subtitle:
        "A branded, secure portal that turns logistics transparency into competitive advantage.",
      steps: [
        {
          num: "Their Dashboard",
          title: "Branded Shipment & Inventory View",
          desc: "Customers log in to your domain, see your logo, and track their shipments, inventory, and ETAs — they never know KGS Technology exists behind the scenes.",
        },
        {
          num: "Self-Service",
          title: "Documents, PODs & Issue Tracking",
          desc: "Download invoices, BOLs, and proof-of-delivery photos. Submit issues with SLA-tracked resolution. Support ticket volume drops 40–60% in the first quarter.",
        },
        {
          num: "Performance",
          title: "Embedded Analytics for Partners",
          desc: "Customer-facing dashboards showing their OTIF rates, transit times, and cost trends — transforming your portal from a tracking tool into a strategic partnership platform.",
        },
      ],
    },
    whyChoose: {
      headline: "Your Competitive Advantage",
      subtitle: "Customer experience is your retention strategy.",
      items: [
        {
          title: "White-Label Perfection",
          desc: "Your logo, your colors, your domain, your emails. Customers interact with your brand — they never see KGS Technology. It's your product, powered by our platform.",
          variant: "accent",
        },
        {
          title: "Self-Service That Actually Works",
          desc: "Customers track shipments, download PODs, view inventory, and submit issues — without calling your ops team. Support tickets drop 40–60% within the first quarter.",
          variant: "teal",
        },
        {
          title: "Multi-Tenant Security",
          desc: "Each customer organization is fully isolated. Users within the same company share access, but no customer can see another's shipments, inventory, or documents.",
          variant: "accent",
        },
        {
          title: "Embedded Analytics",
          desc: "Give customers performance dashboards showing their OTIF rates, transit times, and cost trends — turning your portal from a tracking tool into a strategic partnership platform.",
          variant: "teal",
        },
      ],
    },
    platformHighlight: {
      headline: "Your Brand, Their Loyalty",
      subheadline: "Premium Customer Experience",
      description:
        "Customer Portal gives 3PL operators, account managers, and customer success teams a branded self-service platform that reduces support load, increases transparency, and strengthens customer retention.",
      bullets: [
        "Branded shipment tracking with real-time map view and milestone timeline",
        "Document repository with secure sharing, versioning, and download history",
        "Issue submission and resolution tracking with SLA-based escalation",
        "Customer-level analytics dashboards showing OTIF, transit times, and cost trends",
      ],
      stats: [
        {
          value: "60%",
          label: "Fewer Tickets",
          variant: "accent",
        },
        {
          value: "4.8/5",
          label: "CSAT Score",
          variant: "teal",
        },
        {
          value: "24/7",
          label: "Self-Service",
          variant: "accent",
        },
        {
          value: "100%",
          label: "White-Label",
          variant: "teal",
        },
      ],
    },
  },
  {
    slug: "fairmiles",
    heading: "FairMiles",
    tagline:
      "Find, book, and execute loads smarter — all from one intelligent platform.",
    overViewTagLine: "Maximize Revenue Per Truck",
    overViewDesc:
      "Identify high-value loads faster, compare rates across boards instantly, and reduce time spent searching. FairMiles helps carriers secure better-paying freight while minimizing idle time.",
    overViewOpints: [
      "Smart load filtering with AI-powered recommendations",
      "Profitability insights with per-mile revenue analysis",
      "Instant booking validation and confirmation workflows",
    ],
    capabilities: [
      "Smart load filtering with AI-powered recommendations",
      "Profitability insights with per-mile revenue analysis",
      "Instant booking validation and confirmation workflows",
      "Real-time trip tracking with milestone-based updates",
      "Multi-board aggregation with duplicate detection",
      "Lane preference learning and personalized suggestions",
      "Driver availability management and scheduling tools",
      "Payment tracking and settlement reconciliation",
    ],
    impact: [
      "Reduced empty miles and improved asset utilization",
      "Improved carrier revenue per mile through smart matching",
      "Faster booking cycles (minutes instead of hours)",
      "Better load-to-truck matching with AI recommendations",
      "Reduced time spent searching across multiple platforms",
      "Enhanced profitability visibility for every trip",
    ],
    metrics: [
      {
        value: "25%",
        label: "Reduction in Empty Miles",
      },
      {
        value: "3x",
        label: "Faster Booking Cycles",
      },
      {
        value: "15%",
        label: "Revenue Increase per Truck",
      },
      {
        value: "15+",
        label: "Load Board Integrations",
      },
    ],
    useCases: [
      {
        title: "Load Discovery & Matching",
        description:
          "Aggregate loads from multiple boards with AI-powered matching based on route, equipment, and profitability.",
      },
      {
        title: "Profitability-First Booking",
        description:
          "See estimated profit per mile before booking, factoring in fuel, tolls, deadhead, and detention risk.",
      },
      {
        title: "Trip Execution Tracking",
        description:
          "Track every load from acceptance to delivery with real-time status updates and automated documentation.",
      },
    ],
    integrations: ["LoadSmart", "Direct Shipper APIs"],
    faq: [
      {
        question: "How many load boards does FairMiles aggregate?",
        answer:
          "Currently 15+ load boards with new integrations added monthly based on carrier demand.",
      },
      {
        question: "Is FairMiles free for carriers?",
        answer:
          "We offer a free tier with core features and premium plans for advanced analytics and priority matching.",
      },
    ],
    getStarted: {
      badge: "Carrier Economics",
      headline: "The True Profitability Equation",
      subtitle:
        "Rate-per-mile is a vanity metric. Profit-per-mile is what pays the bills.",
      steps: [
        {
          num: "Discovery",
          title: "15+ Boards, One Feed",
          desc: "Multiple load boards and direct shipper feeds aggregate and deduplicate — surfacing opportunities you'd miss searching each board individually.",
        },
        {
          num: "Analysis",
          title: "Profit After Hidden Costs",
          desc: "Every load shows estimated revenue after fuel, tolls, deadhead repositioning, and detention risk. Book based on actual profit, not headline rates.",
        },
        {
          num: "Execution",
          title: "Book to Invoice, One Platform",
          desc: "Confirm loads, track milestones in real time, capture digital PODs, and generate invoices — no switching between apps or manual paperwork.",
        },
      ],
    },
    whyChoose: {
      headline: "Why Carriers Switch",
      subtitle:
        "Stop burning hours scrolling boards and guessing profitability.",
      items: [
        {
          title: "Profitability-First Matching",
          desc: "Every load shows estimated revenue per mile after fuel, tolls, and deadhead — so you book based on actual profit, not just rate-per-mile that ignores hidden costs.",
          variant: "accent",
        },
        {
          title: "15+ Board Aggregation",
          desc: "FairMiles pulls from multiple load boards and direct shipper feeds — deduplicating listings and surfacing opportunities you'd miss searching each board individually.",
          variant: "teal",
        },
        {
          title: "Lane Learning Engine",
          desc: "The more you book, the smarter FairMiles gets. It learns your preferred lanes, equipment utilization patterns, and scheduling habits — pushing better-fit loads to the top over time.",
          variant: "accent",
        },
        {
          title: "Dead-Mile Killer",
          desc: "AI identifies backhaul and relay opportunities that fill empty return legs — reducing dead miles by up to 25% and turning repositioning costs into revenue.",
          variant: "teal",
        },
      ],
    },
    platformHighlight: {
      headline: "Carrier Intelligence Platform",
      subheadline: "Book Smarter, Earn More",
      description:
        "FairMiles gives owner-operators, fleet managers, and dispatch teams a single platform that replaces manual load board searching with AI-powered discovery, profitability analysis, and end-to-end trip management.",
      bullets: [
        "Unified load feed from 15+ boards with real-time deduplication",
        "Profit-per-mile calculator factoring fuel, tolls, deadhead, and detention",
        "Lane preference learning that improves match quality over time",
        "Trip execution tracker with milestone updates and document generation",
      ],
      stats: [
        {
          value: "25%",
          label: "Fewer Empty Miles",
          variant: "accent",
        },
        {
          value: "3x",
          label: "Faster Booking",
          variant: "teal",
        },
        {
          value: "15%",
          label: "Revenue Uplift",
          variant: "accent",
        },
        {
          value: "15+",
          label: "Board Sources",
          variant: "teal",
        },
      ],
    },
  },
];

export default productsData;
